import { useState, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PixelText } from '../ui/PixelText';
import { PixelButton } from '../ui/PixelButton';
import { PixelPanel } from '../ui/PixelPanel';
import { usePlayerStore } from '../store/player';
import { api, type PullResult } from '../api/client';

type PullState = 'idle' | 'pulling' | 'result' | 'error';

const RARITY_COLORS: Record<string, string> = {
  SSR: '#FFD23A',
  SR: '#FF3AA6',
  R: '#3AF0FF',
  N: '#888888'
};

const DAILY_PULLS_MAX = 2;

function generateRequestId(): string {
  const key = 'stanwars_pull_requestId';
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const id = crypto.randomUUID();
  sessionStorage.setItem(key, id);
  return id;
}

function clearRequestId() {
  sessionStorage.removeItem('stanwars_pull_requestId');
}

export function Gacha() {
  const { t } = useTranslation();
  const me = usePlayerStore(s => s.me);
  const setMe = usePlayerStore(s => s.setMe);
  const [pullState, setPullState] = useState<PullState>('idle');
  const [result, setResult] = useState<PullResult | null>(null);
  const [error, setError] = useState('');

  if (!me) return <Navigate to="/login" replace />;

  const dailyPullsLeft = me.dailyPullsLeft ?? DAILY_PULLS_MAX;

  const doPull = useCallback(async (useFree: boolean) => {
    if (pullState === 'pulling') return;
    setPullState('pulling');
    setError('');
    const requestId = generateRequestId();
    try {
      const res = await api.pull.post({ requestId, useFree });
      await new Promise(r => setTimeout(r, 3000));
      setResult(res);
      setPullState('result');
      clearRequestId();
      try {
        const fresh = await api.me.get();
        setMe(fresh);
      } catch {
        // Non-fatal
      }
    } catch (e) {
      setPullState('error');
      setError(String(e));
      clearRequestId();
    }
  }, [pullState, setMe]);

  const reset = () => { setPullState('idle'); setResult(null); setError(''); };

  return (
    <main className="min-h-screen flex flex-col items-center gap-6 p-6 relative overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none select-none"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: 'url(/stanwars/sprites/groups/twice.png)', backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat', imageRendering: 'pixelated', opacity: 0.12 }}
      />
      <div className="flex items-center gap-3 self-start">
        <Link to="/"><PixelButton variant="ghost" className="text-xs">{t('common.home')}</PixelButton></Link>
        <PixelText size="xl" color="pink">{t('gacha.title')}</PixelText>
      </div>

      {/* Pulling overlay */}
      {pullState === 'pulling' && (
        <PixelPanel className="w-full max-w-sm text-center py-12">
          <PixelText size="2xl" color="pink" className="animate-pulse">{t('gacha.pulling')}</PixelText>
          <PixelText size="sm" color="cyan" className="mt-2 block opacity-70">
            {t('gacha.calling_stage')}
          </PixelText>
        </PixelPanel>
      )}

      {/* Result reveal */}
      {pullState === 'result' && result && (
        <PixelPanel className="w-full max-w-sm text-center py-8">
          <img
            className="w-32 h-32 mx-auto mb-4 border-4 block"
            role="img"
            aria-label={`${result.memberId} sprite`}
            src={`/stanwars/sprites/members/${result.memberId}/portrait.png`}
            alt={result.memberId}
            style={{
              borderColor: RARITY_COLORS[result.rarity] ?? '#888',
              imageRendering: 'pixelated',
              objectFit: 'contain',
              backgroundColor: '#1A1530',
            }}
          />
          <PixelText size="xl" color="yellow">{result.rarity}</PixelText>
          <PixelText size="base" color="cyan" className="block mt-1">{result.memberId}</PixelText>
          {result.isDuplicate && (
            <PixelText size="sm" color="green" className="block mt-2">
              {t('gacha.duplicate', { shards: result.shardsAwarded })}
            </PixelText>
          )}
          <PixelButton onClick={reset} className="mt-6">{t('gacha.pull_again')}</PixelButton>
        </PixelPanel>
      )}

      {/* Error state */}
      {pullState === 'error' && (
        <PixelPanel className="w-full max-w-sm text-center py-6">
          <PixelText color="yellow">{t('gacha.pull_failed')}</PixelText>
          <PixelText size="sm" color="white" className="block mt-1 opacity-70">{error}</PixelText>
          <PixelButton onClick={reset} className="mt-4">{t('gacha.try_again')}</PixelButton>
        </PixelPanel>
      )}

      {/* Idle state */}
      {pullState === 'idle' && (
        <>
          <PixelPanel className="w-full max-w-sm">
            <PixelText size="sm" color="yellow">
              {t('gacha.daily_free_pulls')}: {dailyPullsLeft}/{DAILY_PULLS_MAX}
            </PixelText>
            <PixelText size="sm" color="cyan" className="block mt-1">
              {t('gacha.tickets')}: {me.currencies.tickets}
            </PixelText>
          </PixelPanel>

          <div className="flex flex-col gap-3 w-full max-w-sm">
            <PixelButton
              onClick={() => doPull(true)}
              disabled={dailyPullsLeft <= 0}
            >
              {t('gacha.free_pull_btn', { left: dailyPullsLeft })}
            </PixelButton>
            <PixelButton
              variant="ghost"
              onClick={() => doPull(false)}
              disabled={me.currencies.tickets <= 0}
            >
              {t('gacha.ticket_pull_btn', { count: me.currencies.tickets })}
            </PixelButton>
          </div>
        </>
      )}
    </main>
  );
}
